/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ActivityDrawer
 * --------------
 * The detail panel behind /feed/activity/:id — interactive route map with km
 * markers, the full numbers, an elevation profile built from the recorded
 * altitudes, per-km splits and effort. Opened from a card, or directly from a
 * shared URL (then the activity is fetched, and only returned if it is the
 * viewer's own — see feedApi.fetchActivity).
 *
 * For a GPS activity, Expand (top bar, or the map itself) grows the drawer to
 * full screen — see ActivityExpanded. Escape collapses first, then closes.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import FeedIcon from './FeedIcon';
import RouteMap from './RouteMap';
import ElevationChart from './ElevationChart';
import Splits from './ActivitySplits';
import ActivityExtras from './ActivityExtras';
import ActivityExpanded from './ActivityExpanded';
import { Skeleton } from './FeedParts';
import { useGeoid, useRoute } from '../../pages/feed/useFeedData';
import { isAltitudeMsl, routeToSeaLevel } from '../../pages/feed/geoid';
import {
  typeMeta, displayTitle, formatDateTime, hasRoute, detailStats, NO_ELEVATION_TYPES, maxElevation,
} from '../../pages/feed/feedModel';

export default function ActivityDrawer({ activity, isLoading, notFound, best, onClose }) {
  const closeRef = useRef(null);
  const expandRef = useRef(null);
  const collapseRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const expandedRef = useRef(false);
  expandedRef.current = expanded;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (expandedRef.current) setExpanded(false); else onClose();
    };
    window.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  // Keep focus on the control that swaps places with the one just pressed.
  const prevExpanded = useRef(expanded);
  useEffect(() => {
    if (prevExpanded.current === expanded) return;
    prevExpanded.current = expanded;
    (expanded ? collapseRef : expandRef).current?.focus();
  }, [expanded]);

  const routeWanted = !!activity && hasRoute(activity);
  const { route, isLoading: routeLoading } = useRoute(activity?.id, routeWanted);
  // Altitudes as the app shows them: metres above sea level (see geoid.js).
  // Older recordings need the geoid grid; wait for it rather than flash a
  // value ~86 m off.
  const needsGeoid = routeWanted && !isAltitudeMsl(activity?.meta);
  const { geoid, isLoading: geoidLoading } = useGeoid(needsGeoid);
  const elevLoading = routeLoading || (needsGeoid && geoidLoading);
  const elevRoute = useMemo(
    () => (elevLoading ? [] : routeToSeaLevel(route, activity?.meta, geoid)),
    [elevLoading, route, activity?.meta, geoid],
  );
  const maxElev = useMemo(() => maxElevation(elevRoute), [elevRoute]);
  const canExpand = routeWanted && !isLoading && !notFound;
  const isExpanded = expanded && canExpand;

  let body;
  if (isLoading) {
    body = <div style={{ display: 'grid', gap: 14, marginTop: 20 }}><Skeleton height={40} /><Skeleton height={260} /><Skeleton height={140} /></div>;
  } else if (notFound || !activity) {
    body = (
      <div className="fd-empty" style={{ marginTop: 28 }}>
        <h3>Activity not found</h3>
        {/* <p>It may have been deleted in UpVibe, or it belongs to another account.</p> */}
        <p>It may have been deleted in AthloHive, or it belongs to another account.</p>
      </div>
    );
  } else {
    const meta = typeMeta(activity.type);
    body = (
      <>
        <div className="fd-d-title">
          <span className="fd-tico"><FeedIcon name={meta.icon} /></span>
          <div>
            <h2 id="fd-drawer-title">{displayTitle(activity)}{best && <> <span className="fd-badge" style={{ verticalAlign: 'middle' }}><FeedIcon name="emoji_events" fill />{best}</span></>}</h2>
            <p>{meta.label} · {formatDateTime(activity.startedAt)}</p>
          </div>
        </div>
        {routeWanted && (
          <div className="fd-d-map">
            {routeLoading && <div className="fd-skel" style={{ position: 'absolute', inset: 0, borderRadius: 0 }} />}
            {!routeLoading && route.length >= 2 && <RouteMap route={route} interactive showKm label={`Route of ${displayTitle(activity)}`} />}
            {!routeLoading && route.length < 2 && (
              <div className="fd-media nomap" style={{ height: '100%', margin: 0, border: 0, borderRadius: 0 }}><span><FeedIcon name="location_off" />No GPS route recorded</span></div>
            )}
            {!routeLoading && route.length >= 2 && (
              <button type="button" className="fd-d-mapx" onClick={() => setExpanded(true)} aria-label="Expand route map to full screen">
                <FeedIcon name="open_in_full" />Expand map
              </button>
            )}
          </div>
        )}
        {routeWanted && route.length >= 2 && <p style={{ fontSize: 10.5, color: 'var(--fd-mute)', marginTop: 6 }}>Map © OpenStreetMap contributors</p>}
        <div className="fd-d-grid">
          {detailStats(activity, maxElev).map(([k, v, u]) => (
            <div key={k}><span className="fd-eyebrow">{k}</span><b>{v}{u && <small>{u}</small>}</b></div>
          ))}
        </div>
        {activity.notes && <p className="fd-d-notes">{activity.notes}</p>}
        {routeWanted && !elevLoading && !NO_ELEVATION_TYPES.includes(activity.type) && <ElevationChart route={elevRoute} maxElev={maxElev} />}
        {/* was enabled={footTypes.includes(activity.type) && activity.distanceKm >= 2} — hid splits the app shows (e.g. a walk under 1 km).
            Like the app, any activity type: the card shows whenever splits exist. */}
        <Splits activityId={activity.id} enabled />
        <ActivityExtras activity={activity} />
      </>
    );
  }

  return (
    <>
      <div className="fd-scrim" onClick={onClose} aria-hidden="true" />
      <aside className={`fd-drawer${isExpanded ? ' fd-x' : ''}`} role="dialog" aria-modal="true" aria-labelledby="fd-drawer-title" aria-label="Activity details">
        {isExpanded ? (
          <ActivityExpanded
            activity={activity} best={best} route={route} routeLoading={routeLoading}
            elevRoute={elevRoute} elevLoading={elevLoading} maxElev={maxElev}
            onCollapse={() => setExpanded(false)} onClose={onClose} collapseRef={collapseRef}
          />
        ) : (
          <>
            <div className="fd-d-top">
              <span className="fd-eyebrow">{activity ? formatDateTime(activity.startedAt) : 'Activity'}</span>
              <span className="fd-d-actions">
                {canExpand && (
                  <button type="button" className="fd-pillbtn" onClick={() => setExpanded(true)} ref={expandRef} aria-label="Expand to full screen">
                    <FeedIcon name="open_in_full" /><span className="l">Expand</span>
                  </button>
                )}
                <button type="button" className="fd-iconbtn" onClick={onClose} aria-label="Close" ref={closeRef}><FeedIcon name="close" /></button>
              </span>
            </div>
            {body}
          </>
        )}
      </aside>
    </>
  );
}
